import * as THREE from 'three';
import { splitWall } from '../build.js';
import { createBuilder } from './builder.js';
import { fixtureGrid } from './materials.js';
import { LIGHTS, roomIndex } from './rooms.js';

/**
 * The Crown Ballroom, as a luxury hotel's ballroom.
 *
 * The carpet, the damask and the chandeliers are elsewhere; this is the joinery
 * and the evening. Walnut wainscot with raised panels and a gilded chair rail,
 * pilasters with gilt capitals, a gilded cornice, a coffered ceiling with a
 * warm cove in every bay, sconces between the pilasters, a velvet drape behind
 * the stage and a brass edge round the dance floor.
 *
 * And the room changes once it is set. Handed how far along the room is — from
 * nothing to every table dressed — the chandeliers come down from working
 * light to a warm glow, amber uplights wash the walls and the coves come up.
 * The palette is the property reference's (docs/property-reference.md).
 * That number is a plain fraction worked out by `main.js` from the setup; this
 * file never sees the setup.
 *
 * Everything here stays within a few centimetres of a wall, above everybody's
 * head, or flat on the floor, and nothing is collided with. The stage's
 * skirting stops a foot short of the floor, because under the stage is a
 * hiding place and the way under has to stay visible.
 */

const FT = 0.3048;
const m = (feet) => feet * FT;

export function createBallroom({ plan, environment = null }) {
  const group = new THREE.Group();
  group.name = 'look-ballroom';
  const room = plan.rooms.find((r) => r.zone === 'event');
  if (!room) return { object3D: group, update() {}, dispose() {} };

  const roomAt = roomIndex(plan.rooms);
  const b = createBuilder();
  const disposables = [];
  const mat = (colour, extra = {}) => {
    const made = new THREE.MeshStandardMaterial({ color: colour, roughness: 0.6, ...extra });
    if (extra.metalness && environment) made.envMap = environment;
    disposables.push(made);
    return made;
  };
  const walnut = mat(0x4a2c1a, { roughness: 0.45 });
  const walnutDark = mat(0x33200f, { roughness: 0.5 });
  // Brass, from docs/property-reference.md.
  const gilt = mat(0xc9a35b, { metalness: 0.85, roughness: 0.3 });
  const plaster = mat(0xe8dcc4, { roughness: 0.8 });
  const velvet = mat(0x5a0e1c, { roughness: 1 });
  const brass = mat(0xc9a35b, { metalness: 0.9, roughness: 0.25 });
  const coveGlow = mat(0x201408, { emissive: 0xffc070, emissiveIntensity: 0 });
  const bulbs = mat(0x201408, { emissive: 0xffd9a0, emissiveIntensity: 2.2 });
  const shades = mat(0xf0e0c0, { emissive: 0xffc890, emissiveIntensity: 0.9, roughness: 0.9 });

  const ceiling = m(room.ceiling);
  const [rx0, rz0, rx1, rz1] = room.rect;
  const minX = m(Math.min(rx0, rx1));
  const maxX = m(Math.max(rx0, rx1));
  const minZ = m(Math.min(rz0, rz1));
  const maxZ = m(Math.max(rz0, rz1));
  const inRoom = (x, z) => roomAt(x, z) === room;

  /** Every stretch of wall face that looks into the ballroom, with its doors cut out. */
  const faces = [];
  for (const wall of plan.walls) {
    const [x0, z0] = wall.from;
    const [x1, z1] = wall.to;
    const horizontal = Math.abs(x1 - x0) >= Math.abs(z1 - z0);
    const half = (wall.thickness ?? 0.5) / 2;
    const cross = horizontal ? z0 : x0;
    for (const piece of splitWall({ thickness: 0.5, ...wall })) {
      if (piece.min.y > 0) continue;
      const lo = horizontal ? piece.min.x : piece.min.z;
      const hi = horizontal ? piece.max.x : piece.max.z;
      for (const side of [1, -1]) {
        const mid = (lo + hi) / 2;
        const px = horizontal ? m(mid) : m(cross + side * (half + 1));
        const pz = horizontal ? m(cross + side * (half + 1)) : m(mid);
        if (!inRoom(px, pz)) continue;
        faces.push({ horizontal, cross: m(cross + side * half), side, lo: m(lo), hi: m(hi) });
      }
    }
  }
  // The columns' faces too: each free-standing column is four short walls.
  const columns = plan.props.filter((p) => /^col-/.test(p.id) && inRoom(m((p.rect[0] + p.rect[2]) / 2), m((p.rect[1] + p.rect[3]) / 2)));

  /** A strip on a face, `lo`..`hi` metres along, `y0`..`y1` up, `depth` proud. */
  const strip = (material, f, lo, hi, y0, y1, depth) => {
    if (hi - lo < 0.02) return;
    const along = (lo + hi) / 2;
    const across = f.cross + (f.side * depth) / 2;
    if (f.horizontal) b.box(material, along, (y0 + y1) / 2, across, hi - lo, y1 - y0, depth);
    else b.box(material, across, (y0 + y1) / 2, along, depth, y1 - y0, hi - lo);
  };
  const at = (f, along, depth) => (f.horizontal ? { x: along, z: f.cross + f.side * depth } : { x: f.cross + f.side * depth, z: along });

  const RAIL = 1.0;
  const PILASTER_EVERY = m(16);
  const uplights = [];
  for (const f of faces) {
    // Wainscot: skirting, a walnut field, raised panels, a gilded chair rail.
    strip(walnutDark, f, f.lo, f.hi, 0, 0.22, 0.03);
    strip(walnut, f, f.lo, f.hi, 0.22, RAIL, 0.012);
    const panels = Math.max(1, Math.round((f.hi - f.lo) / 1.2));
    const pw = (f.hi - f.lo) / panels;
    for (let i = 0; i < panels; i += 1) {
      const a = f.lo + i * pw + 0.12;
      const c = f.lo + (i + 1) * pw - 0.12;
      if (c - a < 0.3) continue;
      strip(walnutDark, f, a, c, 0.34, 0.37, 0.024);
      strip(walnutDark, f, a, c, RAIL - 0.13, RAIL - 0.1, 0.024);
      strip(walnutDark, f, a, a + 0.03, 0.34, RAIL - 0.1, 0.024);
      strip(walnutDark, f, c - 0.03, c, 0.34, RAIL - 0.1, 0.024);
    }
    strip(walnut, f, f.lo, f.hi, RAIL, RAIL + 0.06, 0.035);
    strip(gilt, f, f.lo, f.hi, RAIL - 0.02, RAIL, 0.03);
    // The cornice: a gilded band and a plaster cove at the ceiling.
    strip(plaster, f, f.lo, f.hi, ceiling - 0.5, ceiling, 0.1);
    strip(gilt, f, f.lo, f.hi, ceiling - 0.56, ceiling - 0.5, 0.12);
    strip(gilt, f, f.lo, f.hi, m(12), m(12) + 0.04, 0.02);

    // Pilasters, a sconce between each pair, an uplight at the foot of each bay.
    const count = Math.max(1, Math.round((f.hi - f.lo) / PILASTER_EVERY));
    const step = (f.hi - f.lo) / count;
    for (let i = 0; i <= count; i += 1) {
      const p = f.lo + i * step;
      const a = Math.max(f.lo, p - 0.22);
      const c = Math.min(f.hi, p + 0.22);
      if (c - a < 0.3) continue;
      strip(plaster, f, a, c, RAIL + 0.06, ceiling - 0.56, 0.05);
      strip(gilt, f, a - 0.04, c + 0.04, ceiling - 1.0, ceiling - 0.56, 0.08);
      strip(gilt, f, a - 0.02, c + 0.02, RAIL + 0.06, RAIL + 0.18, 0.07);
    }
    for (let i = 0; i < count; i += 1) {
      const mid = f.lo + (i + 0.5) * step;
      if (mid - f.lo < 0.8 || f.hi - mid < 0.8) continue;
      // A sconce: a gilt back plate, an arm, two shaded candles.
      const back = at(f, mid, 0.02);
      const y = m(9);
      if (f.horizontal) b.box(brass, back.x, y, back.z, 0.12, 0.28, 0.03);
      else b.box(brass, back.x, y, back.z, 0.03, 0.28, 0.12);
      for (const off of [-0.18, 0.18]) {
        const c = at(f, mid + off, 0.16);
        b.add(brass, new THREE.CylinderGeometry(0.015, 0.015, 0.2, 6).translate(c.x, y + 0.05, c.z));
        b.add(bulbs, new THREE.SphereGeometry(0.03, 8, 6).translate(c.x, y + 0.2, c.z));
        b.add(shades, new THREE.CylinderGeometry(0.06, 0.1, 0.14, 12, 1, true).translate(c.x, y + 0.24, c.z));
      }
      uplights.push({ ...at(f, mid, 0.02), f, mid });
    }
  }

  // The columns: a walnut plinth, gilt capital and base, gilded arrises.
  for (const col of columns) {
    const x0 = m(Math.min(col.rect[0], col.rect[2]));
    const x1 = m(Math.max(col.rect[0], col.rect[2]));
    const z0 = m(Math.min(col.rect[1], col.rect[3]));
    const z1 = m(Math.max(col.rect[1], col.rect[3]));
    const cx = (x0 + x1) / 2;
    const cz = (z0 + z1) / 2;
    const w = x1 - x0 + 0.06;
    const d = z1 - z0 + 0.06;
    b.box(walnutDark, cx, 0.35, cz, w, 0.7, d);
    b.box(gilt, cx, 0.72, cz, w + 0.02, 0.05, d + 0.02);
    b.box(gilt, cx, ceiling - 0.9, cz, w + 0.1, 0.12, d + 0.1);
    b.box(plaster, cx, ceiling - 0.6, cz, w + 0.16, 0.5, d + 0.16);
    for (const [ex, ez] of [[x0, z0], [x0, z1], [x1, z0], [x1, z1]]) b.box(gilt, ex, ceiling / 2, ez, 0.05, ceiling - 1.6, 0.05);
  }

  // The coffered ceiling: beams on the chandeliers' grid, a warm cove in each bay.
  const grid = fixtureGrid(room, LIGHTS.chandelier.spacing);
  const beamDepth = 0.55;
  const nx = Math.round((maxX - minX) / grid.sx);
  const nz = Math.round((maxZ - minZ) / grid.sz);
  for (let i = 0; i <= nx; i += 1) {
    const x = minX + i * grid.sx;
    b.box(plaster, x, ceiling - beamDepth / 2, (minZ + maxZ) / 2, 0.5, beamDepth, maxZ - minZ);
    b.box(gilt, x, ceiling - beamDepth - 0.02, (minZ + maxZ) / 2, 0.52, 0.04, maxZ - minZ);
  }
  for (let k = 0; k <= nz; k += 1) {
    const z = minZ + k * grid.sz;
    b.box(plaster, (minX + maxX) / 2, ceiling - beamDepth / 2, z, maxX - minX, beamDepth, 0.5);
    b.box(gilt, (minX + maxX) / 2, ceiling - beamDepth - 0.02, z, maxX - minX, 0.04, 0.52);
  }
  for (const p of grid.points) {
    // The cove: a glowing band just inside each bay, set up against the ceiling.
    const w = grid.sx - 0.7;
    const d = grid.sz - 0.7;
    b.box(coveGlow, p.x, ceiling - 0.08, p.z - d / 2, w, 0.04, 0.12);
    b.box(coveGlow, p.x, ceiling - 0.08, p.z + d / 2, w, 0.04, 0.12);
    b.box(coveGlow, p.x - w / 2, ceiling - 0.08, p.z, 0.12, 0.04, d);
    b.box(coveGlow, p.x + w / 2, ceiling - 0.08, p.z, 0.12, 0.04, d);
    // A gilt rose where the chandelier hangs.
    b.add(gilt, new THREE.CylinderGeometry(0.45, 0.35, 0.08, 20).translate(p.x, ceiling - 0.04, p.z));
  }

  // The stage: a velvet drape on the wall behind it, pleated, and skirting
  // round the deck's front and ends that stops short of the floor.
  const deck = plan.props.find((p) => p.id === 'stage-deck');
  if (deck) {
    const x0 = m(Math.min(deck.rect[0], deck.rect[2]));
    const x1 = m(Math.max(deck.rect[0], deck.rect[2]));
    const z0 = m(Math.min(deck.rect[1], deck.rect[3]));
    const z1 = m(Math.max(deck.rect[1], deck.rect[3]));
    const top = m(deck.base + deck.height);
    const wallZ = maxZ - m(0.25);
    const folds = Math.round((x1 - x0 + 1) / 0.18);
    const drape = new THREE.PlaneGeometry(x1 - x0 + 1, ceiling - 1.2, folds * 2, 1);
    const pos = drape.attributes.position;
    for (let i = 0; i < pos.count; i += 1) pos.setZ(i, Math.sin((pos.getX(i) / 0.18) * Math.PI) * 0.06);
    drape.computeVertexNormals();
    drape.rotateY(Math.PI);
    drape.translate((x0 + x1) / 2, (ceiling - 1.2) / 2 + 0.02, wallZ - 0.1);
    b.add(velvet, drape);
    b.box(gilt, (x0 + x1) / 2, ceiling - 1.15, wallZ - 0.12, x1 - x0 + 1.2, 0.08, 0.1);
    // The skirting: box-pleated velvet round the front and both ends, from
    // the deck's lip to a hand's width off the floor. It stops short on
    // purpose: under the stage is a hiding place, and the gap is the tell.
    const hem = 0.3;
    const skirt = (length, pleat) => {
      const g = new THREE.PlaneGeometry(length, top - hem, Math.max(2, Math.round(length / pleat) * 2), 1);
      const p = g.attributes.position;
      for (let i = 0; i < p.count; i += 1) p.setZ(i, (Math.floor(((p.getX(i) + length / 2) / pleat) * 2) % 2) * 0.03);
      g.computeVertexNormals();
      return g.translate(0, (top + hem) / 2, 0);
    };
    b.add(velvet, skirt(x1 - x0, 0.14).rotateY(Math.PI).translate((x0 + x1) / 2, 0, z0 - 0.03));
    b.add(velvet, skirt(z1 - z0, 0.14).rotateY(-Math.PI / 2).translate(x0 - 0.03, 0, (z0 + z1) / 2));
    b.add(velvet, skirt(z1 - z0, 0.14).rotateY(Math.PI / 2).translate(x1 + 0.03, 0, (z0 + z1) / 2));
    b.box(gilt, (x0 + x1) / 2, top + 0.005, z0 - 0.01, x1 - x0, 0.03, 0.04);
    b.box(gilt, x0 - 0.01, top + 0.005, (z0 + z1) / 2, 0.04, 0.03, z1 - z0);
    b.box(gilt, x1 + 0.01, top + 0.005, (z0 + z1) / 2, 0.04, 0.03, z1 - z0);
    b.box(gilt, (x0 + x1) / 2, hem + 0.01, z0 - 0.04, x1 - x0, 0.02, 0.02);
  }

  // A brass edge round the dance floor.
  const floor = plan.props.find((p) => p.id === 'dance-floor');
  if (floor) {
    const x0 = m(Math.min(floor.rect[0], floor.rect[2]));
    const x1 = m(Math.max(floor.rect[0], floor.rect[2]));
    const z0 = m(Math.min(floor.rect[1], floor.rect[3]));
    const z1 = m(Math.max(floor.rect[1], floor.rect[3]));
    const y = m(floor.height) + 0.004;
    b.decal(brass, (x0 + x1) / 2, z0, x1 - x0 + 0.08, 0.08, y);
    b.decal(brass, (x0 + x1) / 2, z1, x1 - x0 + 0.08, 0.08, y);
    b.decal(brass, x0, (z0 + z1) / 2, 0.08, z1 - z0, y);
    b.decal(brass, x1, (z0 + z1) / 2, 0.08, z1 - z0, y);
  }

  const meshes = b.build(group);

  /*
   * Uplights: a warm wash up the wall from the foot of each bay, drawn as a
   * glow on the wall rather than as lights. Faded in as the room is set.
   */
  const wash = makeWashTexture();
  disposables.push(wash);
  const washMaterial = new THREE.MeshBasicMaterial({ map: wash, color: 0xe3a877, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false });
  disposables.push(washMaterial);
  const washes = [];
  for (const u of uplights) {
    const g = new THREE.PlaneGeometry(2.4, ceiling - 1.2);
    g.rotateY(Math.atan2(u.f.horizontal ? 0 : u.f.side, u.f.horizontal ? u.f.side : 0));
    g.translate(u.x, (ceiling - 1.2) / 2 + 0.1, u.z);
    // Just off the damask, and off the panelling in front of it.
    const lift = 0.07;
    g.translate(u.f.horizontal ? 0 : u.f.side * lift, 0, u.f.horizontal ? u.f.side * lift : 0);
    washes.push(g);
  }
  let washMesh = null;
  if (washes.length) {
    const merged = createBuilder();
    for (const g of washes) merged.add(washMaterial, g);
    [washMesh] = merged.build(group, { shadows: false });
    washMesh.renderOrder = 5;
  }

  return {
    object3D: group,
    meshes,
    /**
     * `set` is how far the room is along, 0 to 1. Returns how the room is lit,
     * for the lights that live elsewhere: the chandeliers' glow and the fill
     * baked into the ballroom's own surfaces.
     */
    update({ set = 0 }) {
      const s = Math.max(0, Math.min(1, set));
      washMaterial.opacity = 0.55 * s;
      coveGlow.emissiveIntensity = 0.4 + 2.2 * s;
      return { chandelier: 2.6 - 1.2 * s, fill: 1 - 0.3 * s };
    },
    dispose() {
      for (const mesh of group.children) mesh.geometry?.dispose();
      for (const d of disposables) d.dispose();
    },
  };
}

/** A soft cone of light, brightest at the foot, for an uplight's wash. */
function makeWashTexture() {
  const w = 64;
  const h = 128;
  const px = new Uint8Array(w * h * 4);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const v = y / h; // 0 at the foot: DataTexture rows run bottom-up.
      const u = (x + 0.5) / w - 0.5;
      const spread = 0.12 + v * 0.36;
      const across = Math.max(0, 1 - (u / spread) ** 2);
      const k = across * Math.pow(1 - v, 1.3) * Math.min(1, v * 12 + 0.3);
      const i = (y * w + x) * 4;
      const c = Math.round(255 * k);
      px[i] = c;
      px[i + 1] = c;
      px[i + 2] = c;
      px[i + 3] = 255;
    }
  }
  const t = new THREE.DataTexture(px, w, h, THREE.RGBAFormat);
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  return t;
}
